import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';

const BiometricAuth = ({ onBiometricLogin, isLoading = false }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if biometric authentication is supported
    const checkSupport = async () => {
      if (window.PublicKeyCredential && 
          navigator.credentials && 
          typeof navigator.credentials.create === 'function') {
        try {
          const isAvailable = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setIsSupported(isAvailable);
        } catch (err) {
          console.log('Biometric check failed:', err);
          setIsSupported(false);
        }
      }
    };
    
    checkSupport();
  }, []);

  const handleBiometricLogin = async () => {
    setError('');
    
    // Get remembered email
    const rememberedEmail = localStorage.getItem('userEmail');
    if (!rememberedEmail) {
      setError('No saved credentials found. Please login normally first.');
      return;
    }

    try {
      // Create credential request for biometric auth
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: new Uint8Array(32), // In production, this should come from server
          allowCredentials: [{
            id: new TextEncoder().encode(rememberedEmail),
            type: 'public-key',
            transports: ['internal', 'hybrid']
          }],
          userVerification: 'required',
          timeout: 60000
        }
      });

      if (credential) {
        // In a real implementation, you would verify the credential with your server
        // For now, we'll trigger the OTP flow for the remembered email
        onBiometricLogin(rememberedEmail);
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Biometric authentication was cancelled or failed.');
      } else {
        setError('Biometric authentication is not available. Please login normally.');
      }
    }
  };

  if (!isSupported || !localStorage.getItem('rememberMe')) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-center">
        <div className="w-full h-px bg-gray-300"></div>
        <span className="px-3 text-sm text-gray-500 bg-white">OR</span>
        <div className="w-full h-px bg-gray-300"></div>
      </div>
      
      <Button
        type="button"
        variant="outline"
        className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50"
        onClick={handleBiometricLogin}
        disabled={isLoading}
        data-testid="biometric-login-btn"
      >
        <span className="mr-2">🔐</span>
        {isLoading ? 'Authenticating...' : 'Use Face ID / Fingerprint'}
      </Button>
      
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800 text-sm">{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default BiometricAuth;